/**
 * Seat naming, which differs per mode: Solo faces three bots, Online faces
 * people who typed their own name. Both go through the dictionary, so "Anda"
 * becomes "You" the moment the language changes.
 */

import { useCallback } from "react";
import type { PlayerId } from "@kikik/mahjong-core";
import { useI18n } from "./LocaleProvider";
import type { StringKey } from "@kikik/i18n";

export type NameFor = (playerId: PlayerId) => string;

const BOT_KEY: Record<number, StringKey> = {
  1: "player.bot.south",
  2: "player.bot.west",
  3: "player.bot.north",
};

export interface PlayerNames {
  /** Solo and Tutorial: you plus three deterministic bots. */
  solo: (you: PlayerId) => NameFor;
  /** Online: you plus whoever is sitting there, bots included. */
  table: (you: PlayerId, displayName: (playerId: PlayerId) => string | null) => NameFor;
}

export function usePlayerNames(): PlayerNames {
  const { t } = useI18n();

  const solo = useCallback(
    (you: PlayerId): NameFor =>
      (playerId) => {
        if (playerId === you) return t("player.you");
        const key = BOT_KEY[playerId];
        return key ? t(key) : t("player.generic", { number: playerId + 1 });
      },
    [t],
  );

  const table = useCallback(
    (you: PlayerId, displayName: (playerId: PlayerId) => string | null): NameFor =>
      (playerId) => {
        if (playerId === you) return t("player.you");
        return displayName(playerId) ?? t("player.generic", { number: playerId + 1 });
      },
    [t],
  );

  return { solo, table };
}
